import { ReactNode, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTelegram } from '../hooks/useTelegram';
import { BottomNavigation } from './BottomNavigation';
import { useLogger } from '../utils/logger';

interface LayoutProps {
  children: ReactNode;
}

// Страницы где НЕ показываем нижнее меню (детальные страницы)
const hiddenNavPaths = [
  '/tasks/',         // Страница задачи
  '/protocols/',     // Страница протокола
  '/kpi',            // Страница KPI
  '/settings',       // Настройки
];

// Основные страницы без кнопки назад
const mainPages = ['/', '/projects', '/my-tasks', '/create', '/stats'];

export function Layout({ children }: LayoutProps) {
  const logger = useLogger('Layout');
  const navigate = useNavigate();
  const location = useLocation();
  const { webApp } = useTelegram();

  useEffect(() => {
    logger.componentMount({ pathname: location.pathname });
    return () => {
      logger.componentUnmount();
    };
  }, []);

  useEffect(() => {
    logger.debug('Location changed', { pathname: location.pathname });
  }, [location.pathname]);

  // Показываем нижнее меню везде, кроме детальных страниц
  const shouldHideNav = hiddenNavPaths.some(path => 
    location.pathname.includes(path)
  );
  const showBottomNav = !shouldHideNav;

  useEffect(() => {
    if (!webApp?.BackButton) {
      logger.debug('BackButton not available');
      return;
    }
    
    // Проверяем версию - BackButton не поддерживается в версии 6.0
    const version = parseFloat(webApp.version || '0');
    if (version < 6.1) {
      logger.debug('BackButton not supported in this version', { version });
      return;
    }

    const isMainPage = mainPages.includes(location.pathname);
    logger.debug('BackButton state', { isMainPage, pathname: location.pathname });

    if (isMainPage) {
      webApp.BackButton.hide();
      logger.debug('BackButton hidden');
    } else {
      webApp.BackButton.show();
      logger.debug('BackButton shown');
      
      const handleBack = () => {
        logger.userAction('back_button_click');
        navigate(-1);
      };

      webApp.BackButton.onClick(handleBack);
      
      return () => {
        webApp.BackButton.offClick(handleBack);
        logger.debug('BackButton handler removed');
      };
    }
  }, [location.pathname, navigate, webApp]);

  return (
    <div className="min-h-screen bg-tg-bg text-tg-text safe-area-top">
      <main className={showBottomNav ? 'pb-24' : 'pb-4'}>
        {children}
      </main>
      {showBottomNav && <BottomNavigation />}
    </div>
  );
}

